import React from 'react';
import { Badge, Dropdown, Button, List, Typography, Avatar, Tag, Empty, Divider, Spin } from 'antd';
import { 
  BellOutlined, 
  CheckOutlined, 
  DeleteOutlined,
  ClockCircleOutlined,
  UserOutlined,
  DollarOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  SettingOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { formatDistanceToNow } from 'date-fns';
import useNotifications from '../hooks/useNotifications';

const { Text } = Typography;

const NotificationCenter = () => {
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh,
    createTestNotifications
  } = useNotifications({ limit: 10 });

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'lead':
        return <UserOutlined className="text-blue-500" />;
      case 'deal':
        return <DollarOutlined className="text-green-500" />;
      case 'meeting':
        return <CalendarOutlined className="text-orange-500" />;
      case 'task':
        return <CheckSquareOutlined className="text-purple-500" />;
      case 'system':
        return <SettingOutlined className="text-gray-500" />;
      case 'user':
        return <UserOutlined className="text-indigo-500" />;
      case 'reminder':
        return <ClockCircleOutlined className="text-yellow-500" />;
      default:
        return <BellOutlined className="text-gray-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'blue';
      case 'low':
        return 'green';
      default:
        return 'default';
    }
  };

  const notificationMenu = (
    <div className="w-80 max-h-96 overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <Text strong className="text-lg">Notifications</Text>
          <div className="flex items-center space-x-2">
            <Button 
              type="text" 
              size="small" 
              icon={<ReloadOutlined />}
              onClick={refresh}
              title="Refresh notifications"
            />
            {process.env.NODE_ENV === 'development' && (
              <Button 
                type="text" 
                size="small" 
                onClick={createTestNotifications}
                title="Create test notifications"
              >
                Test
              </Button>
            )}
            {unreadCount > 0 && (
              <Button 
                type="link" 
                size="small" 
                onClick={markAllAsRead}
                className="p-0"
              >
                Mark all as read
              </Button>
            )}
          </div>
        </div>
      </div>

      {notifications.length === 0 ? (
        <div className="p-4">
          <Empty 
            description="No notifications" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : (
        <List
          dataSource={notifications}
          renderItem={(notification) => (
            <List.Item
              className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                !notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
              }`}
              actions={[
                <Button
                  type="text"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={() => markAsRead(notification._id)}
                  disabled={notification.read}
                  title="Mark as read"
                />,
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => deleteNotification(notification._id)}
                  danger
                  title="Delete notification"
                />
              ]}
            >
              <List.Item.Meta
                avatar={getNotificationIcon(notification.type)}
                title={
                  <div className="flex items-center justify-between">
                    <Text strong={!notification.read} className="text-sm">
                      {notification.title}
                    </Text>
                    <Tag color={getPriorityColor(notification.priority)} size="small">
                      {notification.priority}
                    </Tag>
                  </div>
                }
                description={
                  <div>
                    <Text className="text-sm text-gray-600 dark:text-gray-400">
                      {notification.message}
                    </Text>
                    <div className="mt-1">
                      <Text className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                      </Text>
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}

      {notifications.length > 0 && (
        <>
          <Divider className="my-0" />
          <div className="p-3 text-center">
            <Button type="link" size="small">
              View All Notifications
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Dropdown
      popupRender={() => notificationMenu}
      trigger={['click']}
      placement="bottomRight"
      arrow
    >
      <Button
        type="text"
        icon={
          <Badge count={unreadCount} size="small">
            <BellOutlined className="text-lg" />
          </Badge>
        }
        className="flex items-center justify-center"
      />
    </Dropdown>
  );
};

export default NotificationCenter;
